import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { conversationsKeys } from '@/features/conversations/keys';

/**
 * Customer-profile-sidebar data hook (Contract C
 * `GET /api/v1/conversations/{id}/profile`).
 *
 * The endpoint is a read-only aggregate: it combines THIS conversation's own
 * contact identity (`display_name` / `phone_e164` / `peer_id` / `note`) with a
 * cross-account rollup of the customer keyed on the SAME thread key the merged
 * inbox groups on (`COALESCE(phone_e164, peer_id)`), so `first_contact_at`
 * (首次联系) and `conversation_count` (历史会话次数) can never disagree with the
 * aggregate inbox. `account_id` / `account_label` are THIS conversation's
 * owning account (归属账号). Mirrors `CustomerProfileOut` in
 * `hub/app/api/schemas.py`.
 */

// --- Schema (mirror hub/app/api/schemas.py `CustomerProfileOut`) -----------

export const customerProfileSchema = z.object({
  display_name: z.string().nullable(),
  phone_e164: z.string().nullable(),
  peer_id: z.string(),
  note: z.string().nullable(),
  // ISO datetime string — parsed as a plain string here (the sidebar only
  // needs the date part; no Date coercion / date lib pulled in for it).
  first_contact_at: z.string(),
  conversation_count: z.number().int().nonnegative(),
  account_id: z.string(),
  account_label: z.string(),
});

export type CustomerProfile = z.infer<typeof customerProfileSchema>;

/**
 * Fetch the customer-profile aggregate for a single conversation.
 *
 * Uses the `['conversations', 'profile', id]` key (see `keys.ts`) so it shares
 * the conversations root — the same root the chat header's lock-tick
 * subscription watches — without colliding with the `detail` / `lock` slots.
 * `staleTime` mirrors the conversation detail query (30s): the profile changes
 * rarely, so this keeps the sidebar from re-fetching on every open/close.
 */
export function useCustomerProfile(conversationId: string) {
  return useQuery<CustomerProfile, Error>({
    queryKey: conversationsKeys.profile(conversationId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/conversations/${conversationId}/profile`,
      );
      return customerProfileSchema.parse(response.data);
    },
    staleTime: 30_000,
  });
}
